/**
 * numero.ts — a lógica dos campos numéricos do app: conversão, formatação e as
 * três bordas que todo mundo usa. Sem React aqui de propósito: são funções
 * puras, testáveis no Node (`npm run teste:valor`) e usáveis fora de tela.
 *
 * Os campos que consomem isto estão em `components/CustomCamposNumero.tsx`.
 * Doc: .claude/docs/tema/11-campos-valor-rapido.md
 *
 * ┌─ O CONTRATO ────────────────────────────────────────────────────────────┐
 * │ Dentro da tela o valor é sempre uma STRING CANÔNICA com ponto decimal:   │
 * │ `''` (vazio), `'80.66'`, `'-12.50'`. Nunca `number` — float estraga      │
 * │ centavo — e nunca texto pt-BR, que a tela teria de reparsear.            │
 * │                                                                          │
 * │ Nas BORDAS, use sempre estas três funções, e só elas:                    │
 * │   doBanco(x)    o que a API devolveu → valor do campo                    │
 * │   paraBanco(v)  valor do campo       → o que vai no JSON (número | null) │
 * │   paraConta(v)  valor do campo       → número para somar na tela         │
 * │ São o único lugar do app que converte valor. Se um dia o backend passar  │
 * │ a receber DECIMAL como string, muda-se `paraBanco` e mais nada.          │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

/** INTERRUPTOR GLOBAL da digitação rápida. `false` devolve TODO campo ao texto
 *  livre (com o parser já corrigido), sem tocar em nenhuma tela. Rota de fuga
 *  de uma palavra. */
export const VALOR_RAPIDO = true;

/** Teto de dígitos — 15 já é R$ 9.999.999.999.999,99 e ainda cabe em `number`
 *  sem perder precisão. Existe para uma tecla presa não gerar um monstro. */
export const MAX_DIGITOS = 15;

/** Separadores de exibição. pt-BR é o padrão do app (07-formatos-i18n.md); são
 *  configuráveis para o dia em que uma tela precisar de outro formato. */
export const SEPARADOR_PADRAO = { decimal: ',', milhar: '.' } as const;

export type Separadores = { decimal: string; milhar: string };

// ============================================================ rastro de erro
// Nada de erro silencioso: quando o campo recebe algo que não consegue
// interpretar, ele avisa em vez de virar zero caladinho. Em dev, console; em
// produção, o evento é ouvido no main.tsx e vira issue no Sentry.
//
// Este arquivo NÃO importa o Sentry de propósito — assim continua rodando no
// Node, sem navegador, na bateria de testes.

export type ValorEstranho = { motivo: string; recebido: unknown; campo?: string };

export function avisarValorEstranho(dados: ValorEstranho): void {
  const msg = `[valor] ${dados.motivo}: ${JSON.stringify(dados.recebido)}${
    dados.campo ? ` (campo "${dados.campo}")` : ''
  }`;
  // console.warn sempre — é o rastro de quem estiver com o F12 aberto
  console.warn(msg);
  if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent('campo-numero-estranho', { detail: dados }));
  }
}

// ============================================================ helpers puros

/** Agrupa milhar: "1234567" → "1.234.567" */
const milhar = (s: string, sep: string) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

/** Tira zeros à esquerda mantendo pelo menos um dígito: "007" → "7", "0" → "0" */
export const semZerosAEsquerda = (s: string) => s.replace(/^0+(?=\d)/, '');

/**
 * Dígitos crus + sinal → texto exibido no campo.
 * `''` continua `''` (campo vazio é vazio, NÃO "0,00").
 */
export function formatarDigitos(
  digitos: string,
  casas: number,
  negativo = false,
  sep: Separadores = SEPARADOR_PADRAO,
): string {
  if (!digitos) return '';
  const s = digitos.padStart(casas + 1, '0');
  const inteiro = milhar(s.slice(0, s.length - casas), sep.milhar);
  const decimal = casas ? s.slice(s.length - casas) : '';
  const zero = /^0*$/.test(digitos);
  return `${negativo && !zero ? '-' : ''}${inteiro}${casas ? `${sep.decimal}${decimal}` : ''}`;
}

/** Dígitos crus + sinal → string canônica ('80.66'). `''` → `''`. */
export function digitosParaCanonico(digitos: string, casas: number, negativo = false): string {
  if (!digitos) return '';
  const s = digitos.padStart(casas + 1, '0');
  const inteiro = semZerosAEsquerda(s.slice(0, s.length - casas));
  const decimal = casas ? s.slice(s.length - casas) : '';
  const zero = /^0*$/.test(digitos);
  return `${negativo && !zero ? '-' : ''}${inteiro}${casas ? `.${decimal}` : ''}`;
}

/**
 * String canônica → dígitos crus. Aceita o que o backend devolve em DECIMAL
 * ('80.60', '80.6', 80.66 numérico). Casas a mais são ARREDONDADAS, não
 * truncadas — API com 4 casas caindo num campo de 2 vira o centavo certo.
 *
 * Entrada que não é número AVISA (rastro) e devolve vazio — em vez de virar 0.
 */
export function canonicoParaDigitos(
  valor: string | number | null | undefined,
  casas: number,
  campo?: string,
): string {
  const s = String(valor ?? '').trim();
  if (!s) return '';
  const limpo = s.replace(/[^\d.]/g, '');
  if (!limpo) {
    avisarValorEstranho({ motivo: 'valor sem nenhum dígito', recebido: valor, campo });
    return '';
  }
  const [inteiro = '', decimal = ''] = limpo.split('.');
  // só cai no float quando há mais casas do que o campo aceita (aí precisa
  // arredondar); no caminho normal a conversão é exata, por string.
  if (decimal.length > casas) {
    const n = Math.abs(Number(limpo));
    if (!Number.isFinite(n)) {
      avisarValorEstranho({ motivo: 'valor não numérico', recebido: valor, campo });
      return '';
    }
    return semZerosAEsquerda(n.toFixed(casas).replace('.', '')) || '0';
  }
  return semZerosAEsquerda(inteiro + decimal.padEnd(casas, '0')) || '0';
}

/**
 * Parser de texto LIVRE (colagem e modo clássico) — resolve a ambiguidade
 * ponto/vírgula, que é a origem de toda a confusão:
 *
 * - tem vírgula        → vírgula é o decimal, pontos são milhar ("1.234,56")
 * - só pontos, o último seguido de 3 dígitos → tudo milhar ("1.234" = 1234)
 * - só pontos, o último seguido de 1,2 ou 4+ dígitos → é decimal ("80.66" = 80,66)
 * - só dígitos         → número inteiro ("1234" = 1234)
 */
export function parseTextoLivre(texto: string): number | null {
  const s = String(texto ?? '').trim();
  if (!s) return null;
  const negativo = /^\(.*\)$/.test(s) || s.startsWith('-');
  const cru = s.replace(/[^\d.,]/g, '');
  if (!cru) return null;

  let normal: string;
  if (cru.includes(',')) {
    normal = cru.replace(/\./g, '').replace(/,/g, '.');
  } else {
    const partes = cru.split('.');
    const ultima = partes.length > 1 ? partes[partes.length - 1] : '';
    normal =
      partes.length > 1 && ultima.length !== 3
        ? `${partes.slice(0, -1).join('')}.${ultima}`
        : partes.join('');
  }

  const n = Number(normal);
  if (!Number.isFinite(n)) return null;
  return negativo ? -Math.abs(n) : n;
}

/**
 * Anda com a vírgula `casas` posições, **por string** — sem multiplicar nem
 * dividir. Existe porque em JavaScript `0.07 * 100` devolve
 * `7.000000000000001`: qualquer conversão percentual ↔ fração feita com
 * aritmética de float suja o número.
 *
 *   deslocarPonto('0.07', 2)      → '7'        (fração  → percentual)
 *   deslocarPonto('7', -2, 4)     → '0.0700'   (percentual → fração)
 *   deslocarPonto('1234.5', -3)   → '1.2345'   (grama → quilo)
 *
 * `decimais` fixa quantas casas o resultado terá; sem ele, fica o que sobrar do
 * deslocamento. Vindo do campo, as casas já foram limitadas na digitação, então
 * aqui corta em vez de arredondar — arredondar duas vezes é que estraga centavo.
 *
 * Portada do `webapp-rm-ops` (`CamposNumericos.tsx`), onde nasceu.
 */
export function deslocarPonto(
  valor: string | number | null | undefined,
  casas: number,
  decimais?: number,
): string {
  const s = String(valor ?? '').trim();
  if (!s) return '';
  const negativo = s.startsWith('-');
  const limpo = s.replace(/[^\d.]/g, '');
  if (!limpo) {
    avisarValorEstranho({ motivo: 'deslocarPonto recebeu valor sem dígito', recebido: valor });
    return '';
  }

  const [inteiro = '', decimal = ''] = limpo.split('.');
  const digitos = inteiro + decimal;
  // Onde a vírgula fica depois de andar: contada da ESQUERDA, sobre os dígitos.
  const ponto = inteiro.length + casas;

  // Andou para além das bordas: completa com zeros do lado que faltou.
  const esquerda = ponto < 0 ? '0'.repeat(-ponto) + digitos : digitos;
  const pontoOk = ponto < 0 ? 0 : ponto;
  const completo =
    pontoOk > esquerda.length ? esquerda + '0'.repeat(pontoOk - esquerda.length) : esquerda;

  const parteInteira = semZerosAEsquerda(completo.slice(0, pontoOk) || '0');
  let parteDecimal = completo.slice(pontoOk);

  if (decimais !== undefined) {
    parteDecimal =
      parteDecimal.length > decimais
        ? parteDecimal.slice(0, decimais)
        : parteDecimal.padEnd(decimais, '0');
  }

  const zero = /^0*$/.test(completo);
  const sinal = negativo && !zero ? '-' : '';
  return parteDecimal ? `${sinal}${parteInteira}.${parteDecimal}` : `${sinal}${parteInteira}`;
}

// ============================================================ as três bordas

/** Valor do campo → número, para somar/multiplicar na tela. Vazio = 0. */
export function paraConta(valor: string | null | undefined): number {
  const s = String(valor ?? '').trim();
  if (!s) return 0;
  const n = Number(s);
  if (!Number.isFinite(n)) {
    avisarValorEstranho({ motivo: 'paraConta recebeu valor não numérico', recebido: valor });
    return 0;
  }
  return n;
}

/**
 * Valor do campo → o que vai no corpo do JSON. Campo vazio vira `null`, e não
 * zero: "não preenchi" é diferente de "vale zero", e o banco guarda essa
 * diferença.
 */
export function paraBanco(valor: string | null | undefined): number | null {
  const s = String(valor ?? '').trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) {
    avisarValorEstranho({ motivo: 'paraBanco recebeu valor não numérico', recebido: valor });
    return null;
  }
  return n;
}

/**
 * O que a API devolveu → valor do campo (string canônica). Aceita número,
 * string com ponto, `null`. Use ao carregar um formulário de edição.
 */
export function doBanco(
  valorDaApi: string | number | null | undefined,
  casas = 2,
  campo?: string,
): string {
  if (valorDaApi === null || valorDaApi === undefined || valorDaApi === '') return '';
  const n = typeof valorDaApi === 'number' ? valorDaApi : Number(String(valorDaApi).trim());
  if (!Number.isFinite(n)) {
    avisarValorEstranho({
      motivo: 'doBanco recebeu valor não numérico',
      recebido: valorDaApi,
      campo,
    });
    return '';
  }
  return n.toFixed(casas);
}


const LOCALE_BR = 'pt-BR';
const fmtDinheiro = new Intl.NumberFormat(LOCALE_BR, {
  style: 'currency', currency: 'BRL',
});
const fmtNumero = new Intl.NumberFormat(LOCALE_BR, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

// ─── Formatação para EXIBIR (vieram de `format.ts`, que era nome-gaveta) ──────
//
// Não confundir com as três bordas acima (`doBanco`, `paraBanco`, `paraConta`):
// estas só produzem TEXTO para a tela, e o resultado nunca volta para o banco.

export const formatarDinheiro = (v: number | null | undefined): string =>
  fmtDinheiro.format(Number(v ?? 0));

/** 1234.56 -> "1.234,56" */
export const formatarNumero = (v: number | null | undefined): string =>
  fmtNumero.format(Number(v ?? 0));
